import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { purchaseApi } from '@/services/purchaseService';
import { queryKeys } from './queryKeys';
import { useAuthStore } from '@/stores/authStore';

/**
 * Hook to fetch user's purchase history
 */
export const usePurchases = (page: number = 1) => {
  const { isLoggedIn } = useAuthStore();

  return useQuery({
    queryKey: queryKeys.purchases.list(page),
    queryFn: () => purchaseApi.getUserPurchases(page),
    enabled: isLoggedIn, // Only fetch when user is logged in to prevent 401 errors
    staleTime: 60000, // Consider data fresh for 60 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch payment history
 */
export const usePaymentHistory = (page: number = 1) => {
  return useQuery({
    queryKey: queryKeys.payment.all,
    queryFn: () => purchaseApi.getPaymentHistory(page),
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch certificate for an order item
 */
export const useCertificate = (orderItemId: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.purchases.certificate(orderItemId || ''),
    queryFn: () => purchaseApi.fetchCertificate(orderItemId!),
    enabled: !!orderItemId && (options?.enabled ?? true),
  });
};

/**
 * Hook to create purchase/checkout
 */
export const useCreatePurchase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cartItemIds: number[]) => purchaseApi.createPurchase(cartItemIds),
    onSuccess: () => {
      // Invalidate cart and purchase queries after successful purchase
      queryClient.invalidateQueries({ queryKey: queryKeys.cart.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.purchases.all });
    },
  });
};
